import { useEffect, useRef, useState, type RefObject } from "react";
import { DrawingUtils, FaceLandmarker, type NormalizedLandmark } from "@mediapipe/tasks-vision";
import modelUrl from "../../assets/face_landmarker.task?url";
import { createVisionTask } from "./visionRuntime.js";

/**
 * Candidate · the face-mesh overlay on the self-view.
 *
 * WHY THIS IS NOT `@mediapipe/face_mesh`. The first version of this hook used the legacy
 * solutions package with `locateFile: (f) => https://cdn.jsdelivr.net/...`, which reached a THIRD
 * PARTY from the candidate's browser in the middle of their interview. visionRuntime.ts exists
 * precisely to stop that: a CDN fetch is a vendor we never disclosed in the consent flow, one
 * more thing a corporate proxy can block, and the candidate's IP handed to someone else. It also
 * meant a second MediaPipe stack — the app already downloads a wasm runtime and a
 * `face_landmarker` model for gaze and presence, so a candidate could pay ~11.5 MB twice.
 *
 * This version draws the same mesh from the SAME vendored model and the SAME shared runtime, so
 * it costs one task, no network beyond our own origin, and nothing new to disclose. The metric
 * maths below is unchanged from the original, so the numbers a reviewer sees do not move.
 *
 * WHAT LEAVES THE DEVICE: nothing. Landmarks are drawn and discarded frame by frame — no mesh,
 * frame or measurement is uploaded or stored, exactly as with gaze and face presence.
 *
 * KNOWN COST: a strict-mode session also runs the gaze landmarker, so those sessions hold two
 * FaceLandmarker tasks. Both are CPU, `numFaces: 1`, and this one only ticks while the camera is
 * live and the candidate is answering — but sharing one task between the two is the real fix if
 * a mid-range phone ever struggles.
 */

export interface FocusMetrics {
  score: number;
  headAlignment: number;
  stability: number;
  eyeContact: number;
  facialEngagement: number;
}

interface UseFaceMeshResult {
  focusMetrics: FocusMetrics;
  /** A nudge for the candidate. The interview screen decides whether showing it is allowed. */
  focusMessage: string | null;
}

const IDLE: FocusMetrics = {
  score: 100,
  headAlignment: 100,
  stability: 100,
  eyeContact: 100,
  facialEngagement: 100,
};

export function useFaceMesh(
  videoRef: RefObject<HTMLVideoElement | null>,
  canvasRef: RefObject<HTMLCanvasElement | null>,
  phase: string,
): UseFaceMeshResult {
  const [focusMetrics, setFocusMetrics] = useState<FocusMetrics>(IDLE);
  const [focusMessage, setFocusMessage] = useState<string | null>(null);

  const landmarkerRef = useRef<FaceLandmarker | null>(null);
  const drawerRef = useRef<DrawingUtils | null>(null);
  const frameRef = useRef(0);
  const animationRef = useRef<number | null>(null);
  const lastCentersRef = useRef<{ x: number; y: number }[]>([]);
  const lastMessageTimeRef = useRef(0);
  const lastTsRef = useRef(0);

  // Build the task once, from our own origin. A failure here is silent by design: the overlay is
  // an assurance signal, never a gate — a candidate whose browser cannot run wasm still
  // interviews normally, they simply do not see a mesh.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const task = await createVisionTask((fileset) =>
          FaceLandmarker.createFromOptions(fileset, {
            baseOptions: { modelAssetPath: modelUrl, delegate: "CPU" },
            runningMode: "VIDEO",
            numFaces: 1,
          }),
        );
        if (cancelled) {
          task.close();
          return;
        }
        landmarkerRef.current = task;
      } catch {
        landmarkerRef.current = null;
      }
    })();
    return () => {
      cancelled = true;
      landmarkerRef.current?.close();
      landmarkerRef.current = null;
      drawerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (phase !== "recording") {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
      return;
    }

    const processFrame = (): void => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const landmarker = landmarkerRef.current;
      frameRef.current += 1;

      // Every third frame: the mesh is decoration plus a slow-moving score, and the candidate's
      // CPU is also encoding video and running speech.
      if (video && canvas && landmarker && video.readyState >= 2 && frameRef.current % 3 === 0) {
        const ctx = canvas.getContext("2d");
        if (ctx) {
          // detectForVideo demands a strictly increasing timestamp; two reads inside one
          // millisecond would otherwise make MediaPipe throw.
          const ts = Math.max(performance.now(), lastTsRef.current + 1);
          lastTsRef.current = ts;
          let landmarks: NormalizedLandmark[] | undefined;
          try {
            landmarks = landmarker.detectForVideo(video, ts).faceLandmarks[0];
          } catch {
            landmarks = undefined; // a dropped frame is not worth breaking the loop over
          }

          if (video.videoWidth > 0 && (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight)) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            drawerRef.current = null; // DrawingUtils binds to the context's size
          }
          ctx.save();
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          if (landmarks) {
            if (!drawerRef.current) drawerRef.current = new DrawingUtils(ctx);
            const style = { color: "#10b98104", lineWidth: 1 };
            drawerRef.current.drawConnectors(landmarks, FaceLandmarker.FACE_LANDMARKS_TESSELATION, style);
            drawerRef.current.drawConnectors(landmarks, FaceLandmarker.FACE_LANDMARKS_RIGHT_EYE, style);
            drawerRef.current.drawConnectors(landmarks, FaceLandmarker.FACE_LANDMARKS_LEFT_EYE, style);
            drawerRef.current.drawConnectors(landmarks, FaceLandmarker.FACE_LANDMARKS_FACE_OVAL, style);

            const nose = landmarks[1];
            const leftEdge = landmarks[234];
            const rightEdge = landmarks[454];
            if (nose && leftEdge && rightEdge) {
              const minX = Math.min(leftEdge.x, rightEdge.x);
              const maxX = Math.max(leftEdge.x, rightEdge.x);
              const faceWidth = maxX - minX;
              const alignmentRatio = faceWidth > 0 ? (nose.x - minX) / faceWidth : 0.5;
              const headAlignment = Math.max(0, 100 - Math.abs(0.5 - alignmentRatio) * 200);

              const leftIris = landmarks[468];
              const rightIris = landmarks[473];
              const eyeContact =
                leftIris && rightIris
                  ? Math.max(0, 100 - (Math.abs(nose.x - 0.5) + Math.abs(nose.y - 0.5)) * 200)
                  : headAlignment;

              lastCentersRef.current.push({ x: nose.x, y: nose.y });
              if (lastCentersRef.current.length > 10) lastCentersRef.current.shift();
              let stability = 100;
              if (lastCentersRef.current.length === 10) {
                const xs = lastCentersRef.current.map((p) => p.x);
                const ys = lastCentersRef.current.map((p) => p.y);
                const movement = Math.max(...xs) - Math.min(...xs) + (Math.max(...ys) - Math.min(...ys));
                stability = Math.max(0, 100 - movement * 200);
              }

              const score = Math.round(headAlignment * 0.4 + eyeContact * 0.4 + stability * 0.2);
              setFocusMetrics({
                score,
                headAlignment: Math.round(headAlignment),
                stability: Math.round(stability),
                eyeContact: Math.round(eyeContact),
                facialEngagement: score,
              });
              nudge(score < 60);
            }
          } else {
            setFocusMetrics({ score: 0, headAlignment: 0, stability: 0, eyeContact: 0, facialEngagement: 0 });
            nudge(true);
          }
          ctx.restore();
        }
      }
      animationRef.current = requestAnimationFrame(processFrame);
    };

    const nudge = (should: boolean): void => {
      if (!should) return;
      const now = Date.now();
      if (now - lastMessageTimeRef.current <= 4000) return;
      lastMessageTimeRef.current = now;
      setFocusMessage("Kindly maintain your focus towards the camera.");
      setTimeout(() => setFocusMessage(null), 3000);
    };

    processFrame();
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    };
  }, [phase, videoRef, canvasRef]);

  return { focusMetrics, focusMessage };
}
